import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Survey as SurveyType } from '@/features/surveys/classes/SurveyModel'
import { SurveyDemographicOption } from '@/features/surveys/classes/SurveyDemographicOptionModel'
import { Store } from '@/store/Store'
import { userCan } from '@/common/classes/UserUtils'
import { requestSurvey } from '@/features/surveys/api/requests'
import errorHandler from '@/common/lib/errorHandler'
import HgCircularProgressGlobal from '@/common/components/ui/atoms/HgCircularProgressGlobal'
import PageNotFound from '@/common/components/views/PageNotFound'
import { Helmet } from '@/common/components/Helmet'
import { SurveyPreviewContext } from './-context'

export { useSurveyPreview } from './-context'

export const Route = createFileRoute('/app/survey-previews/$surveyId')({
  component: RouteComponent,
})

function RouteComponent() {
  const { surveyId } = Route.useParams()

  const [loading, setLoading] = useState<boolean>(true)
  const [survey, setSurvey] = useState<SurveyType | null>(null)
  const [demographicOptions, setDemographicOptions] = useState<
    SurveyDemographicOption[]
  >([])

  const currentUser = useSelector((state: Store) => state.auth.currentUser)

  // Load survey
  useEffect(() => {
    setLoading(true)
    requestSurvey(Number(surveyId))
      .then((res) => {
        setDemographicOptions(res?.data?.meta?.survey_demographic_options || [])
        setSurvey(res?.data?.data)
      })
      .catch((err) => {
        errorHandler(err, false, false)
        setSurvey(null)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [surveyId])

  if (loading) {
    return <HgCircularProgressGlobal />
  }

  // Compute access
  const userCanView = (survey && survey.published) || userCan(currentUser, 'view_all_surveys')

  if (!userCanView) {
    return <PageNotFound />
  }

  if (!surveyId || !survey) {
    return <PageNotFound />
  }

  return (
    <SurveyPreviewContext.Provider value={{ survey, demographicOptions }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Outlet />
    </SurveyPreviewContext.Provider>
  )
}
